import { ChamferOutline } from "@/components/chamfer";
import { useState, type MouseEvent as ReactMouseEvent } from "react";

import { ComparisonGoalLine } from "@/components/dashboard/dashboard-stats/all-comparison-charts/ComparisonGoalLine";
import { StafferComparisonBar } from "@/components/dashboard/dashboard-stats/all-comparison-charts/StafferComparisonBar";
import { StafferComparisonTooltip } from "@/components/dashboard/dashboard-stats/all-comparison-charts/StafferComparisonTooltip";
import { DashboardKpiCardHeader } from "@/components/dashboard/dashboard-stats/DashboardKpiCardHeader";
import { Card } from "@ui/card";
import { Skeleton } from "@ui/skeleton";

import {
  DASHBOARD_STAT_CARD_HEIGHT_CLASS,
  KPI_GOAL_LINE_PERCENT,
  STAFFER_STAT_GOAL_LINE_PERCENT,
  performanceTierGradientClass,
  screenTimeTier,
} from "@/lib/constants";
import { dashboardKpis } from "@/lib/dashboardKpis";
import { dashboardStafferStats } from "@/lib/dashboardStafferStats";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiComparisonRow,
  DashboardKpiOverview,
  GoalPeriod,
  PerformanceTier,
} from "@/types";

// Card padding-top + the h-10 header + its mb-1. The chart area starts below
// this, so a full-height bar has to reach back up over it.
const HEADER_BLOCK_PX = 58;

const METRIC_COPY: Record<
  DashboardComparisonMetric,
  { accentClassName: string; eyebrow: string; title: string }
> = {
  [DashboardComparisonMetric.TIME_TO_SCREEN]: {
    accentClassName: "border-t-lever-border-error",
    eyebrow: "TTFS · Comparison",
    title: "Time to first screen by staffer",
  },
  [DashboardComparisonMetric.SUBMITS]: {
    accentClassName: "border-t-lever-border-information",
    eyebrow: "Submits · Comparison",
    title: "Submits by staffer",
  },
  [DashboardComparisonMetric.REVENUE]: {
    accentClassName: "border-t-lever-border-success",
    eyebrow: "Revenue · Comparison",
    title: "Revenue by staffer",
  },
};

export const DashboardTeamComparisonsCard = ({
  goalsPeriod,
  isPending,
  metric,
  onMetricChange,
  overview,
}: {
  goalsPeriod: GoalPeriod;
  isPending: boolean;
  metric: DashboardComparisonMetric;
  onMetricChange: (metric: DashboardComparisonMetric) => void;
  overview: DashboardKpiOverview | undefined;
}) => {
  /* React Hooks */
  const [hoveredBar, setHoveredBar] = useState<{
    name: string;
    value: number;
    isRanked: boolean;
    x: number;
    y: number;
  } | null>(null);

  /* Helpers */
  const isTimeToScreen = metric === DashboardComparisonMetric.TIME_TO_SCREEN;
  const copy = METRIC_COPY[metric];
  const goal = dashboardKpis.goalFor({
    metric,
    overview,
    perStaffer: true,
    period: goalsPeriod,
  });
  const periodData = dashboardKpis.periodFor(overview, goalsPeriod);
  const cardTopPercent = dashboardKpis.cardTopPercent(HEADER_BLOCK_PX);

  const valueFor = (row: DashboardKpiComparisonRow) => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if (isTimeToScreen) return row.medianHoursToFirstScreen || 0;
    if (metric === DashboardComparisonMetric.SUBMITS) return row.submits;
    return row.revenue;
  };

  // A staffer with no screens has no time to rank — they'd otherwise read as
  // the fastest on the chart, so they sink to the end like the Staffer Stats
  // card already does.
  const unsortedRows = (periodData ? periodData.comparisons : []).map(
    (row) => ({
      userId: row.userId,
      name: row.name,
      value: valueFor(row),
      isRanked: isTimeToScreen ? row.medianHoursToFirstScreen !== null : true,
    }),
  );
  const rows = [...unsortedRows].sort((a, b) => {
    if (a.isRanked !== b.isRanked) return a.isRanked ? -1 : 1;
    return isTimeToScreen ? a.value - b.value : b.value - a.value;
  });

  const comparisonTier = (value: number): PerformanceTier => {
    if (isTimeToScreen) return screenTimeTier(value);
    if (value >= goal) return PerformanceTier.GREEN;
    if (value >= goal / 2) return PerformanceTier.ORANGE;
    return PerformanceTier.RED;
  };

  const barTopPercent = (value: number): number =>
    isTimeToScreen
      ? dashboardStafferStats.screenChartPercent(value, cardTopPercent)
      : dashboardKpis.goalAnchoredPercent(value, goal);

  const barClassName = (value: number): string => {
    if (isTimeToScreen) {
      return performanceTierGradientClass[comparisonTier(value)];
    }
    // At-or-above goal, matching the tier the hover tooltip colours by.
    if (value >= goal) {
      return performanceTierGradientClass[PerformanceTier.GREEN];
    }
    return "border-t border-lever-border-light bg-gradient-to-b from-lever-surface-neutral/40 via-lever-surface-neutral/20 to-lever-surface-neutral/10";
  };

  /* Handlers */
  const handleNextMetric = () => {
    onMetricChange(dashboardKpis.nextMetric(metric));
  };

  const handleBarHover = (
    event: ReactMouseEvent,
    row: (typeof rows)[number],
  ) => {
    setHoveredBar({
      name: row.name,
      value: row.value,
      isRanked: row.isRanked,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleBarHoverEnd = () => {
    setHoveredBar(null);
  };

  /* Render */
  const renderChart = () => {
    if (isPending) return <Skeleton className="min-h-0 flex-1" />;

    return (
      <>
        <div
          className="relative -mx-4 grid min-h-0 flex-1 gap-x-0.5"
          style={{
            gridTemplateColumns: `repeat(${Math.max(rows.length, 1).toString()}, minmax(0, 1fr))`,
          }}
        >
          <ComparisonGoalLine
            label={dashboardKpis.formatValue(metric, goal)}
            topPercent={
              isTimeToScreen
                ? STAFFER_STAT_GOAL_LINE_PERCENT
                : KPI_GOAL_LINE_PERCENT
            }
          />
          {rows.map((row) => (
            <StafferComparisonBar
              key={row.userId}
              barClassName={barClassName(row.value)}
              isRanked={row.isRanked}
              metric={metric}
              name={row.name}
              onHover={(event) => {
                handleBarHover(event, row);
              }}
              onHoverEnd={handleBarHoverEnd}
              topPercent={barTopPercent(row.value)}
              value={row.value}
            />
          ))}
        </div>
        {hoveredBar ? (
          <StafferComparisonTooltip
            goal={goal}
            isRanked={hoveredBar.isRanked}
            metric={metric}
            name={hoveredBar.name}
            tier={comparisonTier(hoveredBar.value)}
            value={hoveredBar.value}
            x={hoveredBar.x}
            y={hoveredBar.y}
          />
        ) : null}
      </>
    );
  };

  const render = () => (
    <Card
      key={metric}
      className={cn(
        "lever-analysis-card flex min-h-0 w-full min-w-0 flex-col overflow-hidden !rounded-[var(--lever-radius-md)] border-lever-border-light bg-lever-surface-default px-4 pb-0 pt-3.5 font-lever-suisse shadow-none",
        DASHBOARD_STAT_CARD_HEIGHT_CLASS,
      )}
    >
      <ChamferOutline />
      <DashboardKpiCardHeader
        className="mb-1"
        eyebrow={copy.eyebrow}
        onNext={handleNextMetric}
        title={copy.title}
      />
      {renderChart()}
    </Card>
  );

  return render();
};
